import { BillingSeatType, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useQueryUser } from '@heidi/workspace/data';
import { useAtomValue } from 'jotai';

import { useQueryComplimentaryUsage } from '../../../data/query/useQueryComplimentaryUsage';
import { getCommerceBillingUiPorts } from '../../settings/billing/billing-ui-ports';
import {
  classifyClinicianTrialUsage,
  type ClinicianTrialEndedUsage,
  ClinicianTrialEndedUsageClass,
} from './classify-clinician-trial-usage';
import {
  applyClinicianTrialEndDevOverride,
  clinicianTrialEndDevOverrideAtom,
} from './clinician-trial-end-dev-override';
import {
  type ClinicianTrialEndGates,
  useClinicianTrialEndState,
} from './use-clinician-trial-end-state';

export type { ClinicianTrialEndedUsage } from './classify-clinician-trial-usage';

const FORCED_USAGE_PERCENT = {
  [ClinicianTrialEndedUsageClass.MoreThanStandard]: 100,
  [ClinicianTrialEndedUsageClass.WithinStandard]: 50,
} satisfies Record<ClinicianTrialEndedUsageClass, number>;

/**
 * How much a member whose Clinician trial has ended used during it, against the
 * Clinician Standard limit; `null` while loading or before the trial ends.
 * Usage comes from the last ended complimentary cycle, the limit from the v3
 * catalogue.
 */
export function useClinicianTrialEndedUsage(
  gates: ClinicianTrialEndGates
): ClinicianTrialEndedUsage | null {
  const trialEnd = useClinicianTrialEndState(gates);
  const devOverride = applyClinicianTrialEndDevOverride(
    useAtomValue(clinicianTrialEndDevOverrideAtom)
  );
  const { data: user } = useQueryUser();
  const isEnded = trialEnd?.phase === 'ended';
  const usageQuery = useQueryComplimentaryUsage({
    hasUser: !!user,
    enabled: isEnded && devOverride == null,
  });
  const { products } = getCommerceBillingUiPorts();
  const productsQuery = products.useQueryProducts({ heidi2Catalogue: true });

  if (!isEnded) return null;
  if (devOverride != null) {
    return {
      usageClass: devOverride.usageClass,
      usagePercent: FORCED_USAGE_PERCENT[devOverride.usageClass],
    };
  }
  // Wait for both, so the modal never opens on one variant and flips to the other.
  if (usageQuery.isLoading || productsQuery.isLoading) return null;

  const standard = products.getNewProductTierProduct(
    SubscriptionTier.CLINICIAN_PRO,
    productsQuery.data,
    BillingSeatType.STANDARD
  );
  return classifyClinicianTrialUsage({
    consumed: usageQuery.data?.consumed,
    standardLimit: standard?.credit_limit,
  });
}
